import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Link, Navigate } from 'react-router-dom';
import { ArrowLeft, FileDown, MonitorPlay } from 'lucide-react';
import { useAuth } from '../../../AuthContext';
import { SHOWCASE_SECTIONS } from './showcaseContent';
import { ShowcaseSection } from './ShowcaseSection';
import { ResetDemoButton } from './ResetDemoButton';
import { WorkflowSlide } from './WorkflowSlide';
import { ScriptDownloadButton } from './ScriptDownloadButton';
import { WORKFLOWS } from './workflowContent';
import { VoiceoverProvider } from './VoiceoverContext';
import { VoiceoverTools } from './VoiceoverTools';
import { PresenterMode } from './PresenterMode';
import { IntroSlide, OutroSlide } from './BrandSlides';
import './showcase.css';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/admin/demo-showcase`;

const Cover = ({ status }) => (
  <section className="sc-slide sc-cover" data-testid="showcase-cover">
    <img src="/brand/audinexa-horizontal.png" alt="AudiNexa" className="sc-logo" />
    <h1 className="sc-h1">Run your entire hearing clinic on one screen.</h1>
    <p className="sc-lead">
      AudiNexa brings appointments, hearing tests, reports, hearing-aid trials, inventory, repairs, billing and
      analytics together for audiology clinics in India — across every branch.
    </p>
    <div className="sc-cover-meta">
      <span>Shown with demo clinic <strong>Hear All</strong> · Bengaluru · 3 branches</span>
      {status?.patients ? <span data-testid="showcase-demo-count">{status.patients} demo patients</span> : null}
    </div>
  </section>
);

const Contents = () => (
  <section className="sc-slide sc-toc" data-testid="showcase-contents">
    <h2 className="sc-title">What’s inside</h2>
    <ol className="sc-toc-list">
      {SHOWCASE_SECTIONS.map((s, i) => (
        <li key={s.id}>
          <a href={`#sc-${s.id}`} data-testid={`showcase-toc-${s.id}`}>
            <span className="sc-step-no">{i + 1}</span>{s.title}
            <em>{(WORKFLOWS[s.id] || []).length} workflows</em>
          </a>
        </li>
      ))}
    </ol>
  </section>
);

const pad = (n) => String(n).padStart(2, '0');

const buildSlides = () => [
  { kind: 'intro', ref: '00.0' }, { kind: 'cover' }, { kind: 'toc' },
  ...SHOWCASE_SECTIONS.flatMap((s, i) => [
    { kind: 'section', s, i, ref: `${pad(i + 1)}.0` },
    ...(WORKFLOWS[s.id] || []).map((wf, j) => ({ kind: 'wf', s, i, j, wf, ref: `${pad(i + 1)}.${j + 1}` })),
  ]),
  { kind: 'outro', ref: '99.0' },
];

const Slide = ({ slide, status }) => {
  if (slide.kind === 'intro') return <IntroSlide />;
  if (slide.kind === 'outro') return <OutroSlide />;
  if (slide.kind === 'cover') return <Cover status={status} />;
  if (slide.kind === 'toc') return <Contents />;
  if (slide.kind === 'section') return <ShowcaseSection section={slide.s} index={slide.i} refId={slide.ref} />;
  return <WorkflowSlide wf={slide.wf} sectionTitle={slide.s.title} sectionNo={slide.i + 1} index={slide.j} refId={slide.ref} />;
};

const Toolbar = ({ status, onReset, onPresent }) => (
  <header className="sc-toolbar" data-testid="showcase-toolbar">
    <Link to="/admin/dashboard" className="sc-btn sc-btn-ghost" data-testid="showcase-back-link">
      <ArrowLeft size={16} /> Admin
    </Link>
    <div className="sc-toolbar-right">
      {status?.last_reset_at && (
        <span className="sc-muted" data-testid="showcase-last-reset">
          Demo reset {new Date(status.last_reset_at).toLocaleString('en-IN')}
        </span>
      )}
      <ResetDemoButton onDone={onReset} />
      <ScriptDownloadButton />
      <VoiceoverTools />
      <button type="button" className="sc-btn sc-btn-ghost" onClick={onPresent} data-testid="showcase-present-btn">
        <MonitorPlay size={16} /> Present
      </button>
      <button type="button" className="sc-btn sc-btn-primary" onClick={() => window.print()}
              data-testid="showcase-download-pdf-btn">
        <FileDown size={16} /> Download as PDF deck
      </button>
    </div>
  </header>
);

export default function DemoShowcasePage() {
  const { user } = useAuth();
  const [status, setStatus] = useState(null);
  const [presenting, setPresenting] = useState(false);
  const isFounder = user?.role === 'founder';
  const slides = useMemo(buildSlides, []);
  const closePresenter = useCallback(() => setPresenting(false), []);
  const load = useCallback(() => {
    axios.get(`${API}/status`).then((r) => setStatus(r.data)).catch(() => setStatus(null));
  }, []);
  useEffect(() => { if (isFounder) load(); }, [isFounder, load]);
  useEffect(() => { document.title = 'AudiNexa — Product Showcase'; }, []);
  useEffect(() => {
    const el = document.createElement('style');
    el.textContent = '@media print { @page { size: A4 landscape; margin: 0; } }';
    document.head.appendChild(el);
    return () => el.remove();
  }, []);

  if (!isFounder) return <Navigate to="/" replace />;
  return (
    <VoiceoverProvider>
      <div className="sc-root" data-testid="demo-showcase-page">
        <Toolbar status={status} onReset={load} onPresent={() => setPresenting(true)} />
        <main className="sc-deck">
          {slides.map((sl) => <Slide key={sl.ref || sl.kind} slide={sl} status={status} />)}
        </main>
        {presenting && (
          <PresenterMode slides={slides} onClose={closePresenter}
                         render={(sl) => <Slide slide={sl} status={status} />} />
        )}
      </div>
    </VoiceoverProvider>
  );
}
